// ============================================================
// SideQuests - Route metadata registry (single source of truth)
//
// This module is intentionally DEPENDENCY FREE. It imports nothing, so:
//   * the runtime head manager (useSeoHead) can apply it on every
//     client-side navigation, and
//   * the build-time generator (scripts/site/site-content.mjs) mirrors
//     the exact same titles/descriptions for the static HTML shells,
//   * and the test suite can import it directly to prove the static
//     HTML and the hydrated head agree.
//
// Rules enforced here:
//   * indexable routes are self-canonical with "index, follow"
//   * application/private routes are "noindex, follow" with NO canonical
//   * unknown routes fall back to NOT_FOUND_SEO (never homepage defaults)
//   * every indexable route has unique title + description
// ============================================================

export const SITE_ORIGIN = 'https://trysidequests.com';
export const SITE_NAME = 'SideQuests';
export const OG_IMAGE = SITE_ORIGIN + '/og-card.png';
export const OG_IMAGE_TYPE = 'image/png';
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;

/** Branded 1200x630 card used when a route has no dedicated artwork. */
const BRAND_IMAGE_ALT = 'SideQuests - find local Quests in Rhode Island and get paid';

export interface RouteSeo {
  /** Route path, always leading-slash and never trailing-slash (except '/'). */
  path: string;
  title: string;
  description: string;
  /** Content of the robots meta tag. */
  robots: string;
  /** Whether the route should emit a self-referential canonical link. */
  selfCanonical: boolean;
  ogType: 'website' | 'article';
  image: string;
  imageAlt: string;
}

export interface TownInfo {
  name: string;
  region: string;
}

interface SeoOptions {
  path: string;
  title: string;
  description: string;
  robots?: string;
  selfCanonical?: boolean;
  ogType?: 'website' | 'article';
  image?: string;
  imageAlt?: string;
}

function seo(opts: SeoOptions): RouteSeo {
  return {
    path: opts.path,
    title: opts.title,
    description: opts.description,
    robots: opts.robots ?? 'index, follow',
    selfCanonical: opts.selfCanonical ?? true,
    ogType: opts.ogType ?? 'website',
    image: opts.image ?? OG_IMAGE,
    imageAlt: opts.imageAlt ?? BRAND_IMAGE_ALT,
  };
}

/**
 * Application / auth / payment / thin surfaces. These are reachable by
 * crawlers (no robots.txt Disallow) precisely so the noindex directive can
 * be read, but they must never be indexed and must never claim a canonical.
 */
export const NOINDEX_ROUTE_PREFIXES = [
  '/payments',
  '/payouts',
  '/invite',
  '/community',
  '/leaderboard',
  '/inbox',
  '/settings',
  '/my-quests',
  '/dashboard',
  '/onboarding',
  '/notifications',
  '/earnings',
  '/admin',
  '/auth',
  '/profile',
  '/reports',
  '/tos',
  '/quest/',
  // Posting is a signed-in application surface. The bare /post route carries
  // its own descriptive metadata (below); this prefix keeps nested posting
  // routes such as /post/payment-success from falling back to Not Found.
  '/post/',
];

function noindexMeta(path: string): RouteSeo {
  return seo({
    path,
    title: 'SideQuests',
    description: 'This area of SideQuests is part of the signed-in application experience.',
    robots: 'noindex, follow',
    selfCanonical: false,
  });
}

/** Canonical URL for a route (root keeps its single trailing slash). */
export function canonicalFor(path: string): string {
  if (path === '/' || path === '') return SITE_ORIGIN + '/';
  return SITE_ORIGIN + (path.endsWith('/') ? path.slice(0, -1) : path);
}

export const HOME_SEO: RouteSeo = seo({
  path: '/',
  title: 'SideQuests - Find Quests. Get Paid. Level Up.',
  description:
    'SideQuests connects people who need things done with people ready to earn. ' +
    'Post local quests, find nearby work, get paid securely, earn XP, build reputation, and level up.',
});

/** Town page description - mirrored byte-for-byte by the static generator. */
export function townDescription(name: string, region: string): string {
  return (
    'Find local Quests in ' + name + ', ' + region + '. Post or answer the Call on SideQuests gigs near ' +
    name + ' - secure Stripe payments, identity-checked Questers, and local XP.'
  );
}

export function townTitle(name: string): string {
  return name + ', Rhode Island - Local Quests & Questers | SideQuests';
}

/** Minimal shape the registry needs to title a /businesses/{slug} page. */
export interface BusinessInfo {
  name: string;
  municipality: string;
}

/**
 * Per-business title. Mirrored byte-for-byte by the static generator so the
 * SPA shell and the hydrated head can never disagree.
 */
export function businessTitle(name: string): string {
  return name + ' - Featured Local Business in Rhode Island | SideQuests';
}

/** Per-business description. Mirrored byte-for-byte by the generator. */
export function businessDescription(name: string, municipality: string): string {
  const where = municipality ? municipality + ', Rhode Island' : 'Rhode Island';
  return (
    'SideQuests is featuring ' + name + ' in ' + where + '. See why we picked them, ' +
    'where to find them, and what is happening on SideQuests nearby.'
  );
}

/**
 * The indexable route registry. Order is not significant; lookups are exact
 * matches on the normalized pathname.
 */
const ROUTE_SEO: Record<string, RouteSeo> = {
  '/': HOME_SEO,
  // ⭐ /quests is the CANONICAL marketplace route. /feed is legacy-only and
  // permanently redirects here (see firebase.json + App.tsx), so it is no
  // longer a separate indexable entry.
  '/quests': seo({
    path: '/quests',
    title: 'Find Quests - SideQuests',
    description:
      "Browse SideQuests' open Quest market: yard work, moving, cleaning, errands and " +
      'furniture assembly. Search, filter by category and price, and find work near you.',
  }),
  // ChatGPT plugin launch page. The primary call to action on this page
  // navigates to the exact published plugin URL, in the current tab.
  '/gpt': seo({
    path: '/gpt',
    title: 'SideQuests on ChatGPT - Open the SideQuests Plugin',
    description:
      'SideQuests is now on ChatGPT. Find local Quests without leaving ChatGPT, ' +
      'then visit SideQuests to take the next step.',
    imageAlt: 'Open SideQuests in ChatGPT',
  }),
  '/meetQ': seo({
    path: '/meetQ',
    title: 'Meet Q - SideQuests',
    description:
      'Meet Q, the SideQuests mascot: an old Bristol quahog with a long memory and a dry sense of humor. ' +
      'Read his backstory and how he connects to SideQuests.',
    imageAlt: 'Q, the SideQuests mascot',
  }),
  '/map': seo({
    path: '/map',
    title: 'Explore the Map - SideQuests',
    description:
      "Explore SideQuests' live Quest map: find nearby work by city and category. Exact locations " +
      'stay private until a Quester is approved - the map shows an approximate city-level view.',
    imageAlt: 'SideQuests map of Rhode Island towns and open Quests',
  }),
  '/businesses': seo({
    path: '/businesses',
    title: 'Featured Local Businesses - SideQuests',
    description:
      'Rhode Island businesses featured by SideQuests. Meet the shops, makers, and ' +
      'neighborhood spots we are highlighting, and see what is happening nearby.',
  }),
  '/about': seo({
    path: '/about',
    title: 'About SideQuests - A Rhode Island Local-Task Marketplace',
    description:
      'SideQuests is a Rhode Island local-task marketplace built in the East Bay. Post a local Quest ' +
      'or earn money completing one. Bristol, Warren, and Barrington lead the launch.',
  }),
  '/how-it-works': seo({
    path: '/how-it-works',
    title: 'How It Works - SideQuests',
    description:
      'Post a Quest, Questers Answer the Call, you pick your Quester, and the work gets done. ' +
      'Payment is authorized through Stripe and captured after you approve the work. ' +
      'There is no automatic release: if you do not approve, the payment stays outstanding. ' +
      'Get anything done in 4 simple steps with SideQuests.',
  }),
  '/faq': seo({
    path: '/faq',
    title: 'FAQ - SideQuests',
    description:
      'Answers about SideQuests payments, the 18% service fee with a $5 minimum, the $10 minimum ' +
      'Quest reward, the identity check, Questers getting paid, cancel policy, tips, safety, refunds, and more.',
  }),
  '/support': seo({
    path: '/support',
    title: 'Support - SideQuests',
    description:
      'Get help with SideQuests, a Rhode Island local-task marketplace. Email support@trysidequests.com, ' +
      'or review the Privacy Policy, Terms of Service, Trust and Safety, and Prohibited Quests pages.',
  }),
  '/trust-and-safety': seo({
    path: '/trust-and-safety',
    title: 'Trust and Safety - SideQuests',
    description:
      'How SideQuests keeps every marketplace safe: Stripe Identity verification, GPS tracking during ' +
      'active quests, reviews and ratings, secure Stripe payments, and an 18% ' +
      'service fee ($5 minimum) that never cuts into what Questers earn.',
  }),
  '/prohibited-quests': seo({
    path: '/prohibited-quests',
    title: 'Prohibited Quests - SideQuests',
    description:
      'What is not allowed on SideQuests: work that legally requires a professional, trade, or ' +
      'occupational license, registration, certification, or permit. See examples and how review works.',
  }),
  '/privacy': seo({
    path: '/privacy',
    title: 'Privacy Policy - SideQuests',
    description:
      'SideQuests Privacy Policy: the information we collect, how we use it, the SideQuests plugin ' +
      'for ChatGPT and what its tools send and receive, municipality-level location handling, public ' +
      'Quest listings and reference photos, Firebase and Google Cloud, error monitoring, data sharing, ' +
      'retention, security, your choices and controls, and how to contact us.',
  }),
  '/terms': seo({
    path: '/terms',
    title: 'Terms of Service - SideQuests',
    description:
      'SideQuests Terms of Service: platform role, independent contractor relationship, liability ' +
      'limits, the 18% service fee, Stripe Connect payments, dispute resolution, and contact information.',
  }),
  // ⭐ Posting is a signed-in application surface: never indexed, never
  // self-canonical, but the document title must describe the page instead of
  // inheriting "Page not found - SideQuests" from the Not Found fallback.
  '/post': seo({
    path: '/post',
    title: 'Post a Quest - SideQuests',
    description:
      'Post a local Quest on SideQuests: describe the work, set the reward, and authorize payment ' +
      'securely through Stripe. Your card is authorized now and captured after you approve the work. ' +
      'There is no automatic release: if you do not approve, the payment stays outstanding.',
    robots: 'noindex, follow',
    selfCanonical: false,
  }),
};

/** Noindex metadata for unknown routes. Never carries a canonical. */
export const NOT_FOUND_SEO: RouteSeo = seo({
  path: '',
  title: 'Page not found - SideQuests',
  description:
    'That SideQuests page could not be found. Head back to the homepage, browse Quests on the Rhode ' +
    'Island map, or contact SideQuests support for help.',
  robots: 'noindex, follow',
  selfCanonical: false,
});

/** Normalize a pathname: strip query/hash, collapse, drop trailing slash. */
export function normalizePath(pathname: string): string {
  let path = (pathname || '/').split('?')[0].split('#')[0];
  if (!path.startsWith('/')) path = '/' + path;
  if (path.length > 1) path = path.replace(/\/+$/, '');
  return path === '' ? '/' : path;
}

function isNoindexPath(path: string): boolean {
  if (path === '/invite' || path === '/community' || path === '/leaderboard') return true;
  for (const prefix of NOINDEX_ROUTE_PREFIXES) {
    if (prefix.endsWith('/')) {
      if (path.startsWith(prefix)) return true;
    } else if (path === prefix || path.startsWith(prefix + '/')) {
      return true;
    }
  }
  return false;
}

/**
 * Resolve metadata for a route.
 *
 * Returns `undefined` for genuinely unknown routes so the caller can apply
 * NOT_FOUND_SEO explicitly (an unknown route must never inherit homepage
 * metadata). `lookupTown` resolves /ri/{slug} pages; when it is omitted or
 * the slug is unknown, the town route is treated as unknown.
 */
export function resolveRouteSeo(
  pathname: string,
  lookupTown?: (slug: string) => TownInfo | undefined,
  lookupBusiness?: (slug: string) => BusinessInfo | undefined
): RouteSeo | undefined {
  const path = normalizePath(pathname);

  const exact = ROUTE_SEO[path];
  if (exact) return exact;

  if (path.startsWith('/ri/')) {
    const slug = path.slice('/ri/'.length);
    const town = slug && !slug.includes('/') ? lookupTown?.(slug) : undefined;
    if (!town) return undefined;
    return seo({
      path,
      title: townTitle(town.name),
      description: townDescription(town.name, town.region),
      imageAlt: town.name + ', Rhode Island on SideQuests',
    });
  }

  // Per-business pages follow the same contract as town pages: when the caller
  // cannot supply the record (unknown slug, or a build with no lookup), the
  // route is treated as UNKNOWN so it falls back to NOT_FOUND_SEO rather than
  // inheriting homepage metadata.
  if (path.startsWith('/businesses/')) {
    const slug = path.slice('/businesses/'.length);
    const business = slug && !slug.includes('/') ? lookupBusiness?.(slug) : undefined;
    if (!business) return undefined;
    return seo({
      path,
      title: businessTitle(business.name),
      description: businessDescription(business.name, business.municipality),
      imageAlt: business.name + ' on SideQuests',
    });
  }

  if (isNoindexPath(path)) return noindexMeta(path);

  return undefined;
}

/** Every route this module can resolve without a town lookup. */
export function indexablePaths(): string[] {
  return Object.keys(ROUTE_SEO);
}
